import { useQuery } from '@tanstack/react-query';
import { fetchSliders } from '../api/sliders';
import { Slider } from '../types/sliders';
import { isCurrentlyActive } from '@/lib/date-utils';

// Fisher-Yates shuffle for randomizing array
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function useSliders() {
  return useQuery<Slider[]>({
    queryKey: ['sliders'],
    queryFn: async () => {
      const response = await fetchSliders();
      // Filter sliders: if scheduled, check date range; otherwise always show
      const activeSliders = response.data.filter((slider) => {
        if (slider.SliderScheduled && slider.SliderStartDate && slider.SliderEndDate) {
          return isCurrentlyActive(slider.SliderStartDate, slider.SliderEndDate);
        }
        // Non-scheduled sliders are always shown
        return true;
      });

      // Separate scheduled and non-scheduled sliders
      const scheduled = activeSliders.filter((s) => s.SliderScheduled);
      const nonScheduled = activeSliders.filter((s) => !s.SliderScheduled);

      // Sort scheduled by start date (earliest first)
      scheduled.sort((a, b) => {
        const dateA = a.SliderStartDate ? new Date(a.SliderStartDate).getTime() : 0;
        const dateB = b.SliderStartDate ? new Date(b.SliderStartDate).getTime() : 0;
        return dateA - dateB;
      });

      // Randomize non-scheduled sliders
      const randomizedNonScheduled = shuffleArray(nonScheduled);

      // Return scheduled first, then randomized non-scheduled
      return [...scheduled, ...randomizedNonScheduled];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}


